import {assertAndContinue} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import {FolderSection, StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import AppDispatcher from 'lib/Dispatcher';
import {getDefaultFolderSearchParams} from 'nav/sidepanel/listing/FolderSearchParams';
import {getDefaultTypesForSection} from 'nav/sidepanel/listing/ProjectsSearchParams';
import {getDefaultStorablesSearchParams} from 'nav/sidepanel/listing/StorablesSearchParams';
import {Thunk} from 'redux-stores/Store';
import * as RegistryUtil from 'registry/RegistryUtil';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import * as FilterUtil from 'search/FilterUtil';
import {FilterKey, keyToEmptyFilter, Operator, SearchParams} from 'search/SearchConstants';
import {SEARCH_TYPE_BY_SECTION} from 'search/SearchTypes';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import * as SearchUtil from 'search/SearchUtil';

import * as SidepanelActions from './SidepanelActions';

export function openListing(section: NavConstants.Section, searchParams: SearchParams): Thunk<void> {
  return (dispatch) => {
    SidepanelActions.openSection(section);
    // Ensure that we don't accidentally show a previously loaded saved search as still active.
    dispatch(SearchUIReduxActions.setSelectedSavedSearch(undefined, section));
    dispatch(
      SearchUIReduxActions.updateParamsAndSearch(
        section,
        SEARCH_TYPE_BY_SECTION[section],
        searchParams,
        getEmptyFilterErrors(searchParams.filters)
      )
    );
  };
}

export function openSectionAndListFolder(
  section: NavConstants.Section,
  searchParams: SearchParams,
  folder: StringIDFolder
): Thunk<void> {
  assertAndContinue(
    section !== NavConstants.Section.STORAGE,
    'openSectionAndListFolder should not be used for STORAGE section'
  );
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_LIST_SINGLE_SOURCE,
    section,
    folder,
  });
  return openListing(section, searchParams);
}

export function openStorageSectionWithRegistry(
  section: NavConstants.Section,
  searchParams: SearchParams,
  registryId: string
): Thunk<void> {
  assertAndContinue(
    section === NavConstants.Section.STORAGE,
    'openStorageSectionWithRegistry should only be used for STORAGE section'
  );
  SidepanelActions.setStorablesSidepanelActiveRegistry(registryId);
  return openListing(section, searchParams);
}

export function listFolder(folder: StringIDFolder, searchParams?: SearchParams): Thunk<void> {
  let section;
  const defaultSearchParams = getDefaultFolderSearchParams(folder);
  if (RegistryUtil.isRegistry(folder.source)) {
    section = NavConstants.Section.REGISTRIES;
  } else {
    section = NavConstants.Section.PROJECT_ITEMS;
  }

  return openSectionAndListFolder(section, searchParams || defaultSearchParams, folder);
}

export function listLocation(locationId: string, registryId: string): Thunk<void> {
  const defaultSearchParams = getDefaultStorablesSearchParams(registryId);
  const searchParams = {
    ...defaultSearchParams,
    filters: FilterUtil.replaceByKey(defaultSearchParams.filters, {
      key: FilterKey.PARENT_STORABLE,
      operator: Operator.IS,
      value: locationId,
    }),
  };
  return (dispatch) => {
    SidepanelActions.setStorablesSidepanelActiveRegistry(registryId);
    dispatch(openListing(NavConstants.SECTIONS.STORAGE, searchParams));
  };
}

export function listFolderSection(folder: StringIDFolder, section: FolderSection): Thunk<void> {
  return (dispatch) => {
    dispatch(
      listFolder(
        folder,
        SearchUtil.mergeFiltersToSearchParams(getDefaultFolderSearchParams(folder), [
          {
            ...keyToEmptyFilter(FilterKey.TYPES),
            value: getDefaultTypesForSection(FolderSection.ALL),
          },
          {
            ...keyToEmptyFilter(FilterKey.FOLDER_SECTION),
            value: FolderSection.ALL,
          },
        ])
      )
    );
  };
}
